import { Horse, Trophy, Target, ChartBar, Globe, Star, Television, Lightning, Users, TrendUp } from "@phosphor-icons/react";
import { useState, useEffect } from "react";
import { fetchPredictionStats, fetchLiveRaces } from "../lib/api";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/c9755703-c0c8-4c44-9e75-4d569fefe3eb/images/c987ad7fa3f56011c771ff73853d8be3b1166f63a2807ca016343ad2f9e8eee8.jpeg";

export default function ShowcardPage() {
  const [stats, setStats] = useState(null);
  const [raceCount, setRaceCount] = useState(0);
  const [runnerCount, setRunnerCount] = useState(0);

  useEffect(() => {
    fetchPredictionStats().then(setStats).catch(console.error);
    fetchLiveRaces().then((races) => {
      setRaceCount(races?.length || 0);
      const total = races?.reduce((acc, r) => acc + (r.runners?.length || 0), 0) || 0;
      setRunnerCount(total);
    }).catch(console.error);
  }, []);

  const todayDate = new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <div data-testid="showcard-page" className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header Bar */}
      <div className="flex items-center justify-between px-6 py-4 max-w-2xl mx-auto">
        <div className="flex items-center gap-3">
          <img src="/logo-40.png" alt="Racing Form Guide" className="w-12 h-12" />
          <div>
            <h1 className="text-lg font-black tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>Racing Form Guide</h1>
            <p className="text-[11px] text-zinc-500 font-mono">The smart racing form app</p>
          </div>
        </div>
        <span className="bg-[#E12B00] text-white px-3 py-1 text-[10px] font-mono font-bold uppercase tracking-widest flex items-center gap-1.5">
          <span className="w-2 h-2 bg-white rounded-full live-pulse" />
          Live
        </span>
      </div>

      {/* Hero Screenshot */}
      <div className="max-w-2xl mx-auto px-4 mb-6">
        <div className="relative overflow-hidden border border-zinc-800">
          <img src={HERO_IMG} alt="Racing Form Guide App" className="w-full" />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0a0a0a] to-transparent h-20" />
        </div>
      </div>

      {/* Live Win Rate - Updates Daily */}
      <div className="max-w-2xl mx-auto px-4 mb-6">
        <div data-testid="showcard-live-winrate" className="border border-[#D4AF37]/30 bg-[#D4AF37]/5 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[#D4AF37] text-xs font-mono uppercase tracking-widest flex items-center gap-1.5">
              <Target weight="bold" size={14} />
              Live Prediction Win Rate
            </h3>
            <span className="text-[10px] text-zinc-500 font-mono">{todayDate}</span>
          </div>

          {stats && stats.total_finished > 0 ? (
            <div className="grid grid-cols-4 gap-3 text-center">
              <div>
                <p className="font-mono text-3xl font-bold text-white">{stats.accuracy_pct}%</p>
                <p className="text-[10px] text-zinc-500 font-mono uppercase mt-1">Win Rate</p>
              </div>
              <div>
                <p className="font-mono text-3xl font-bold text-[#008A27]">{stats.correct}</p>
                <p className="text-[10px] text-zinc-500 font-mono uppercase mt-1">Correct</p>
              </div>
              <div>
                <p className="font-mono text-3xl font-bold text-[#E12B00]">{stats.wrong}</p>
                <p className="text-[10px] text-zinc-500 font-mono uppercase mt-1">Wrong</p>
              </div>
              <div>
                <p className="font-mono text-3xl font-bold text-[#D4AF37]">{stats.top3_pct}%</p>
                <p className="text-[10px] text-zinc-500 font-mono uppercase mt-1">Top 3</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-2">
              <p className="font-mono text-3xl font-bold text-zinc-400">--</p>
              <p className="text-[10px] text-zinc-500 font-mono mt-1">No finished races yet today - stats update as races complete</p>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-zinc-800 text-center">
            <p className="text-[10px] text-zinc-600 font-mono">
              Based on {stats?.total_finished || 0} finished races today &middot; Updates every race
            </p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="max-w-2xl mx-auto px-4 mb-6">
        <div className="grid grid-cols-3 gap-3">
          <div className="border border-zinc-800 p-4 text-center">
            <Globe weight="bold" size={18} className="mx-auto text-[#D4AF37] mb-2" />
            <p className="font-mono text-2xl font-bold">{raceCount || "61+"}</p>
            <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider mt-1">Live Races</p>
          </div>
          <div className="border border-zinc-800 p-4 text-center">
            <Horse weight="bold" size={18} className="mx-auto text-[#D4AF37] mb-2" />
            <p className="font-mono text-2xl font-bold">{runnerCount || "500+"}</p>
            <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider mt-1">Runners</p>
          </div>
          <div className="border border-zinc-800 p-4 text-center">
            <ChartBar weight="bold" size={18} className="mx-auto text-[#D4AF37] mb-2" />
            <p className="font-mono text-2xl font-bold">32</p>
            <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider mt-1">Bookmakers</p>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-2xl mx-auto px-4 mb-6">
        <h3 className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-3">Features</h3>
        <div className="space-y-2">
          {[
            { icon: <Target weight="bold" size={18} className="text-[#008A27]" />, title: "AI Win Chance %", desc: "Our algorithm analyses form, ratings, trainer/jockey/owner stats and 32 bookmaker markets to calculate each horse's win probability." },
            { icon: <Users weight="bold" size={18} className="text-cyan-400" />, title: "Jockey & Owner Win Rates", desc: "Every runner shows their jockey's career win rate and owner's historical success rate, factored into our prediction algorithm.", badge: "NEW" },
            { icon: <Television weight="bold" size={18} className="text-[#E12B00]" />, title: "Watch Live In-App", desc: "Cast live race streams directly inside the app with our Betfred-style in-app player. ITV, Sky Sports Racing, Racing TV.", badge: "NEW" },
            { icon: <TrendUp weight="bold" size={18} className="text-amber-400" />, title: "Most Likely + Most Backed", desc: "See our AI's top pick alongside where the real bookmaker money is going across 32 platforms." },
            { icon: <Lightning weight="bold" size={18} className="text-[#D4AF37]" />, title: "Live Odds from 32 Bookmakers", desc: "Real-time prices from Betfred, Bet365, William Hill, Coral, Ladbrokes, Paddy Power and more." },
            { icon: <Globe weight="bold" size={18} className="text-blue-400" />, title: "Worldwide Racing", desc: "Every meeting from GB, IRE, AUS, USA, Japan, UAE, France and more - all in one dashboard." },
            { icon: <Star weight="fill" size={18} className="text-[#D4AF37]" />, title: "Official Ratings & Form", desc: "OFR, RPR, and full form string analysis for every runner so you can see the full picture." },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-3 border border-zinc-800 p-3.5">
              <div className="mt-0.5 flex-shrink-0">{f.icon}</div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-sm">{f.title}</h4>
                  {f.badge && <span className="bg-[#E12B00] text-white px-1.5 py-0.5 text-[8px] font-mono font-bold uppercase">{f.badge}</span>}
                </div>
                <p className="text-xs text-zinc-400 mt-0.5 leading-relaxed">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-2xl mx-auto px-4 mb-6 text-center py-4">
        <a
          href="/"
          data-testid="showcard-cta"
          className="inline-block bg-[#E12B00] text-white font-mono text-sm font-bold uppercase tracking-wider px-10 py-4 hover:bg-[#C52400] transition-colors"
        >
          Open Racing Form Guide
        </a>
        <p className="text-xs text-zinc-500 mt-3">Free to use &middot; No download required</p>
      </div>

      {/* Enjoying */}
      <div className="max-w-2xl mx-auto px-4 mb-8">
        <div data-testid="showcard-feedback" className="border border-zinc-800 p-6 text-center">
          <h3 className="text-lg mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Enjoying Racing Form Guide?</h3>
          <p className="text-sm text-zinc-400 mb-4">A one-tap answer helps us make it better for every UK racing fan.</p>
          <div className="flex items-center justify-center gap-4">
            <button data-testid="feedback-no" className="border border-zinc-700 text-zinc-400 px-6 py-2 text-sm hover:border-zinc-500 transition-colors">Not really</button>
            <button data-testid="feedback-yes" className="bg-[#E12B00] text-white px-6 py-2 text-sm hover:bg-[#C52400] transition-colors flex items-center gap-1.5">
              <span className="text-base">&#10084;</span> Yes!
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-zinc-800 py-4 text-center text-[10px] text-zinc-600">
        <p>&copy; {new Date().getFullYear()} RAW COMPANY TECH LIMITED &middot; Company No. 17349121 &middot; England & Wales</p>
      </div>
    </div>
  );
}
